import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { createFileRoute } from "@tanstack/react-router";
import {
  BookOpen,
  Check,
  Copy,
  Eye,
  FolderPlus,
  GitPullRequest,
  Hand,
  KeyRound,
  ListChecks,
  MousePointerClick,
  Puzzle,
  ShieldCheck,
  SquareTerminal,
  Timer,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

/**
 * `/download`: lair, Hub's app for your terminal, what it does and how to install it. The
 * install command is served by this same Hub (`public/install.sh`).
 */

export const Route = createFileRoute("/_app/download")({
  component: DownloadPage,
});

const FEATURES: Array<{ icon: ReactNode; key: string }> = [
  { icon: <ListChecks className="h-5 w-5" />, key: "planner" },
  { icon: <SquareTerminal className="h-5 w-5" />, key: "developer" },
  { icon: <FolderPlus className="h-5 w-5" />, key: "creator" },
  { icon: <ShieldCheck className="h-5 w-5" />, key: "audit" },
  { icon: <GitPullRequest className="h-5 w-5" />, key: "mr" },
  { icon: <Hand className="h-5 w-5" />, key: "takeover" },
  { icon: <MousePointerClick className="h-5 w-5" />, key: "openFromHub" },
  { icon: <Eye className="h-5 w-5" />, key: "watch" },
  { icon: <Puzzle className="h-5 w-5" />, key: "skills" },
  { icon: <Timer className="h-5 w-5" />, key: "tracker" },
  { icon: <KeyRound className="h-5 w-5" />, key: "subscription" },
];

const SETUP = ["folder", "signIn", "links", "skills", "tracker"] as const;

/** Command, and the key of its description under `miscDownload.cmd`. */
const COMMANDS: Array<[string, string]> = [
  ["lair", "lair"],
  ["lair planner [project]", "planner"],
  ['lair developer "<task>"', "developerNew"],
  ["lair developer [ticket]", "developerTicket"],
  ["lair creator", "creator"],
  ["lair tracker sync", "trackerSync"],
  ["lair setup", "setup"],
  ["lair update", "update"],
  ["lair config projects-dir <folder>", "configProjectsDir"],
];

function CopyCommand({ command }: { command: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/50 p-3 font-mono text-sm">
      <span className="text-muted-foreground select-none">$</span>
      <code className="flex-1 overflow-x-auto whitespace-nowrap" data-testid="install-command">
        {command}
      </code>
      <Button
        size="sm"
        variant="outline"
        onClick={() => {
          void navigator.clipboard.writeText(command).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          });
        }}
      >
        {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
        {copied ? t("miscDownload.copied") : t("miscDownload.copy")}
      </Button>
    </div>
  );
}

function DownloadPage() {
  const { t } = useTranslation();
  const command = `curl -fsSL ${window.location.origin}/install.sh | sh`;
  return (
    <div className="mx-auto max-w-4xl space-y-10 py-6" data-testid="download-page">
      <section className="space-y-4">
        <div className="flex items-center gap-3">
          <img src="/apps/lair.svg" alt="" className="h-14 w-14" />
          <div>
            <h1 className="text-2xl font-semibold">lair</h1>
            <p className="text-muted-foreground">{t("miscDownload.intro")}</p>
          </div>
        </div>
        <CopyCommand command={command} />
        <p className="text-sm text-muted-foreground">
          <Trans
            i18nKey="miscDownload.install"
            components={{
              code: <code />,
              claude: (
                // biome-ignore lint/a11y/useAnchorContent: Trans fills the text
                <a
                  href="https://docs.claude.com/en/docs/claude-code/setup"
                  target="_blank"
                  rel="noreferrer"
                  className="underline"
                />
              ),
            }}
          />
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold">{t("miscDownload.whatItDoes")}</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          {FEATURES.map((f) => (
            <Card key={f.key}>
              <CardContent className="flex gap-3 p-4">
                <div className="mt-0.5 shrink-0 text-primary">{f.icon}</div>
                <div className="space-y-1">
                  <p className="font-medium">{t(`miscDownload.features.${f.key}.title`)}</p>
                  <p className="text-sm text-muted-foreground [&_code]:rounded [&_code]:bg-muted [&_code]:px-1">
                    <Trans
                      i18nKey={`miscDownload.features.${f.key}.body`}
                      components={{ code: <code /> }}
                    />
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section className="grid gap-6 md:grid-cols-2">
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">{t("miscDownload.theSetup")}</h2>
          <ol className="list-decimal space-y-2 pl-5 text-sm text-muted-foreground">
            {SETUP.map((s) => (
              <li key={s}>{t(`miscDownload.setup.${s}`)}</li>
            ))}
          </ol>
        </div>
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">{t("miscDownload.commands")}</h2>
          <dl className="space-y-2 text-sm">
            {COMMANDS.map(([cmd, what]) => (
              <div key={cmd}>
                <dt>
                  <code className="rounded bg-muted px-1.5 py-0.5">{cmd}</code>
                </dt>
                <dd className="mt-0.5 text-muted-foreground">{t(`miscDownload.cmd.${what}`)}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <BookOpen className="h-4 w-4" />
        {t("miscDownload.more")}
      </p>
    </div>
  );
}
